import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { LogOut } from 'lucide-react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { getPostLogoutRedirectUri } from '@/auth/MsalConfig';
import { Button } from '@/components/ui/button';
import { clearAllBrowserData } from '@/utils/clearBrowserData';
export function SidebarUserCard({ onAfterSignOut }) {
    const { instance, accounts } = useMsal();
    const isMsalAuthed = useIsAuthenticated();
    const navigate = useNavigate();
    const profile = (() => {
        const acc = accounts[0];
        if (!acc)
            return { name: 'User', email: '', initials: 'U', mode: 'Signed in' };
        const name = acc.name ?? acc.username ?? 'User';
        const email = acc.username ?? '';
        const initials = name
            .split(/\s+/)
            .map((p) => p[0])
            .join('')
            .slice(0, 2)
            .toUpperCase() || 'U';
        return { name, email, initials, mode: 'Microsoft 365' };
    })();
    const signOut = () => {
        void (async () => {
            onAfterSignOut?.();
            const account = instance.getActiveAccount() ?? accounts[0] ?? null;
            // Wipe local data first — logoutRedirect navigates away, so nothing
            // after it would run.
            try {
                await instance.clearCache();
            }
            catch {
                /* ignore */
            }
            await clearAllBrowserData();
            if (isMsalAuthed && account) {
                try {
                    // Redirect, never popup: popups are blocked on Hostinger and MSAL
                    // throws block_nested_popups if this window was opened as a popup.
                    await instance.logoutRedirect({
                        account,
                        postLogoutRedirectUri: getPostLogoutRedirectUri(),
                    });
                    return;
                }
                catch {
                    /* fall through to local navigation */
                }
            }
            navigate('/login', { replace: true });
        })();
    };
    return (_jsx("div", { className: "rounded-xl border border-border/70 bg-muted/30 p-2.5", children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-emerald-700 text-xs font-bold text-primary-foreground shadow-inner", "aria-hidden": true, children: profile.initials }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "truncate text-sm font-semibold leading-tight text-foreground", children: profile.name }), _jsx("p", { className: "truncate text-[11px] text-muted-foreground", children: profile.email || profile.mode })] }), _jsx(Button, { type: "button", variant: "ghost", size: "icon", "aria-label": "Sign out", onClick: signOut, className: "h-8 w-8 shrink-0 rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive", children: _jsx(LogOut, { className: "h-4 w-4" }) })] }) }));
}
